/**
 * CreateDatabaseConnectionModal Component
 *
 * Modal for creating a new database connection.
 * Supports both self-managed and Ketrics-managed connection types.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useCreateDatabaseConnection } from '../hooks/useCreateDatabaseConnection';
import { useManagedSqlDbs } from '../../managed-sql-databases/hooks/useManagedSqlDbs';
import { z } from 'zod';
import { ConnectionType, DatabaseEngine } from '../../../types/database-connection.types';
import type { DatabaseConnectionCreateDTO } from '../../../types/database-connection.types';

/**
 * Generate database connection code from name
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
};

// Form validation schema with conditional validation by connection type
const createDatabaseConnectionFormSchema = z.object({
  connectionType: z.enum(['SELF_MANAGED', 'KETRICS_MANAGED']),
  dbConnectionName: z
    .string()
    .min(1, 'Connection name is required')
    .max(100, 'Connection name must not exceed 100 characters'),
  dbConnectionCode: z
    .string()
    .min(3, 'Code must be at least 3 characters')
    .max(50, 'Code must not exceed 50 characters')
    .regex(/^[a-z0-9-]+$/, 'Code must contain only lowercase letters, numbers, and hyphens'),
  description: z.string().max(500, 'Description must not exceed 500 characters').optional(),
  engine: z.enum(['SQL_SERVER', 'MYSQL', 'POSTGRESQL', 'ORACLE']).optional(),
  host: z.string().max(255, 'Host must not exceed 255 characters').optional(),
  port: z
    .string()
    .optional()
    .refine((val) => {
      if (!val || val === '') return true;
      const num = parseInt(val);
      return !isNaN(num) && num >= 1 && num <= 65535;
    }, 'Port must be between 1 and 65535'),
  database: z.string().max(128, 'Database name must not exceed 128 characters').optional(),
  username: z.string().max(128, 'Username must not exceed 128 characters').optional(),
  password: z.string().max(1024, 'Password must not exceed 1024 characters').optional(),
  ssl: z.boolean().optional(),
  isActive: z.boolean().optional(),
  portForwarding: z.boolean().optional(),
  sshHost: z.string().max(255, 'SSH host must not exceed 255 characters').optional(),
  sshPort: z
    .string()
    .optional()
    .refine((val) => {
      if (!val || val === '') return true;
      const num = parseInt(val);
      return !isNaN(num) && num >= 1 && num <= 65535;
    }, 'SSH port must be between 1 and 65535'),
  sshUsername: z.string().max(128, 'SSH username must not exceed 128 characters').optional(),
  managedSqlDbCode: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.connectionType === 'SELF_MANAGED') {
    if (!data.engine) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Database engine is required', path: ['engine'] });
    }
    if (!data.host || data.host.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Host is required', path: ['host'] });
    }
    if (!data.port || data.port.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Port is required', path: ['port'] });
    }
    if (!data.database || data.database.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Database name is required', path: ['database'] });
    }
    if (!data.username || data.username.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Username is required', path: ['username'] });
    }
    if (!data.password || data.password.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Password is required', path: ['password'] });
    }
  } else if (data.connectionType === 'KETRICS_MANAGED') {
    if (!data.managedSqlDbCode || data.managedSqlDbCode.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Managed database is required', path: ['managedSqlDbCode'] });
    }
  }
});

type CreateDatabaseConnectionFormData = z.infer<typeof createDatabaseConnectionFormSchema>;

interface CreateDatabaseConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

const databaseEngineOptions = [
  { value: 'SQL_SERVER', label: 'SQL Server' },
  { value: 'MYSQL', label: 'MySQL' },
  { value: 'POSTGRESQL', label: 'PostgreSQL' },
  { value: 'ORACLE', label: 'Oracle' },
];

const defaultPorts: Record<string, string> = {
  SQL_SERVER: '1433',
  MYSQL: '3306',
  POSTGRESQL: '5432',
  ORACLE: '1521',
};

/**
 * CreateDatabaseConnectionModal component
 */
export const CreateDatabaseConnectionModal: React.FC<CreateDatabaseConnectionModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateDatabaseConnectionFormData>({
    resolver: zodResolver(createDatabaseConnectionFormSchema),
    defaultValues: {
      connectionType: 'SELF_MANAGED',
      dbConnectionName: '',
      dbConnectionCode: '',
      description: '',
      engine: 'POSTGRESQL',
      host: '',
      port: '5432',
      database: '',
      username: '',
      password: '',
      ssl: true,
      isActive: true,
      portForwarding: false,
      sshHost: '',
      sshPort: '22',
      sshUsername: '',
      managedSqlDbCode: '',
    },
  });

  const watchName = watch('dbConnectionName');
  const watchEngine = watch('engine');
  const watchPortForwarding = watch('portForwarding');
  const watchConnectionType = watch('connectionType');

  const isSelfManaged = watchConnectionType === 'SELF_MANAGED';
  const isManaged = watchConnectionType === 'KETRICS_MANAGED';

  // Fetch available managed SQL databases for the picker
  const { data: managedSqlDbs } = useManagedSqlDbs(tenantId, { status: 'available' });
  const availableManagedDbs = managedSqlDbs || [];

  // Auto-generate code from name
  useEffect(() => {
    if (autoGenerateCode && watchName) {
      const generatedCode = generateCodeFromName(watchName);
      setValue('dbConnectionCode', generatedCode);
    }
  }, [watchName, autoGenerateCode, setValue]);

  // Update default port when engine changes
  useEffect(() => {
    if (watchEngine && defaultPorts[watchEngine]) {
      setValue('port', defaultPorts[watchEngine]);
    }
  }, [watchEngine, setValue]);

  const { mutate: createDatabaseConnection, isPending } = useCreateDatabaseConnection(tenantId, {
    onSuccess: () => {
      setSuccessMessage('Database connection created successfully!');
      setErrorMessage('');
      setTimeout(() => {
        reset();
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: CreateDatabaseConnectionFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    let connectionData: DatabaseConnectionCreateDTO;

    if (data.connectionType === 'SELF_MANAGED') {
      connectionData = {
        connectionType: ConnectionType.SELF_MANAGED,
        dbConnectionName: data.dbConnectionName,
        dbConnectionCode: data.dbConnectionCode,
        description: data.description || undefined,
        engine: data.engine as DatabaseEngine,
        host: data.host,
        port: parseInt(data.port!),
        database: data.database,
        username: data.username,
        password: data.password,
        ssl: data.ssl,
        isActive: data.isActive ?? true,
        portForwarding: data.portForwarding ?? false,
        sshHost: data.portForwarding && data.sshHost ? data.sshHost : undefined,
        sshPort: data.portForwarding && data.sshPort ? parseInt(data.sshPort) : undefined,
        sshUsername: data.portForwarding && data.sshUsername ? data.sshUsername : undefined,
      };
    } else {
      connectionData = {
        connectionType: ConnectionType.KETRICS_MANAGED,
        dbConnectionName: data.dbConnectionName,
        dbConnectionCode: data.dbConnectionCode,
        description: data.description || undefined,
        engine: DatabaseEngine.POSTGRESQL, // Always PostgreSQL for managed
        managedSqlDbCode: data.managedSqlDbCode,
        isActive: data.isActive ?? true,
      };
    }

    createDatabaseConnection(connectionData);
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      setAutoGenerateCode(true);
      setShowPassword(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Header>Create Database Connection</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Connection Type Selector */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Connection Type</h3>
            <div className="grid grid-cols-2 gap-3">
              <label
                className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                  isSelfManaged
                    ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-600'
                    : 'border-gray-300 bg-white hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  value="SELF_MANAGED"
                  {...register('connectionType')}
                  className="sr-only"
                />
                <div className="flex flex-col">
                  <span className={`block text-sm font-medium ${isSelfManaged ? 'text-primary-900' : 'text-gray-900'}`}>
                    Self-Managed
                  </span>
                  <span className={`mt-1 text-xs ${isSelfManaged ? 'text-primary-700' : 'text-gray-500'}`}>
                    Connect to your own database server with custom credentials
                  </span>
                </div>
              </label>
              <label
                className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                  isManaged
                    ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-600'
                    : 'border-gray-300 bg-white hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  value="KETRICS_MANAGED"
                  {...register('connectionType')}
                  className="sr-only"
                />
                <div className="flex flex-col">
                  <span className={`block text-sm font-medium ${isManaged ? 'text-primary-900' : 'text-gray-900'}`}>
                    Ketrics-Managed
                  </span>
                  <span className={`mt-1 text-xs ${isManaged ? 'text-primary-700' : 'text-gray-500'}`}>
                    Use a Ketrics-managed PostgreSQL database with IAM authentication
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Basic Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Connection Name */}
              <FormField label="Connection Name" required error={errors.dbConnectionName?.message}>
                <Input
                  {...register('dbConnectionName')}
                  placeholder="Production Database"
                  error={!!errors.dbConnectionName}
                />
              </FormField>

              {/* Connection Code */}
              <FormField label="Connection Code" required error={errors.dbConnectionCode?.message}>
                <Input
                  {...register('dbConnectionCode')}
                  placeholder="prod-db"
                  error={!!errors.dbConnectionCode}
                  disabled={autoGenerateCode}
                />
                <div className="mt-2 flex items-center">
                  <Checkbox
                    id="autoGenerateCode"
                    checked={autoGenerateCode}
                    onChange={(e) => setAutoGenerateCode(e.target.checked)}
                  />
                  <label htmlFor="autoGenerateCode" className="ml-2 text-sm text-gray-600">
                    Auto-generate code
                  </label>
                </div>
              </FormField>
            </div>

            {/* Description */}
            <div className="mt-4">
              <FormField label="Description" error={errors.description?.message}>
                <Textarea
                  {...register('description')}
                  placeholder="Describe the purpose of this database connection..."
                  rows={2}
                  error={!!errors.description}
                />
              </FormField>
            </div>
          </div>

          {/* Self-Managed Connection Details */}
          {isSelfManaged && (
            <>
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-4">Connection Details</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {/* Database Engine */}
                  <FormField label="Database Engine" required error={errors.engine?.message}>
                    <Select {...register('engine')} error={!!errors.engine}>
                      {databaseEngineOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  {/* Host */}
                  <FormField label="Host" required error={errors.host?.message}>
                    <Input
                      {...register('host')}
                      placeholder="localhost or db.example.com"
                      error={!!errors.host}
                    />
                  </FormField>

                  {/* Port */}
                  <FormField label="Port" required error={errors.port?.message}>
                    <Input
                      {...register('port')}
                      type="number"
                      min="1"
                      max="65535"
                      error={!!errors.port}
                    />
                  </FormField>

                  {/* Database Name */}
                  <FormField label="Database Name" required error={errors.database?.message}>
                    <Input
                      {...register('database')}
                      placeholder="mydb"
                      error={!!errors.database}
                    />
                  </FormField>

                  {/* Username */}
                  <FormField label="Username" required error={errors.username?.message}>
                    <Input
                      {...register('username')}
                      placeholder="db_user"
                      error={!!errors.username}
                    />
                  </FormField>

                  {/* Password */}
                  <FormField label="Password" required error={errors.password?.message}>
                    <div className="relative">
                      <Input
                        {...register('password')}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        error={!!errors.password}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Password will be encrypted at rest using KMS.
                    </p>
                  </FormField>
                </div>

                {/* SSL Connection */}
                <div className="mt-4 flex items-center">
                  <Checkbox
                    id="ssl"
                    {...register('ssl')}
                    defaultChecked
                  />
                  <label htmlFor="ssl" className="ml-2 text-sm text-gray-900">
                    Use SSL/TLS connection
                  </label>
                  <span className="ml-2 text-xs text-gray-500">(recommended for production)</span>
                </div>
              </div>

              {/* SSH Port Forwarding */}
              <div>
                <div className="flex items-center mb-4">
                  <Checkbox
                    id="portForwarding"
                    {...register('portForwarding')}
                  />
                  <label htmlFor="portForwarding" className="ml-2 text-sm font-medium text-gray-900">
                    Enable SSH Port Forwarding
                  </label>
                </div>

                {watchPortForwarding && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 pl-6 border-l-2 border-gray-200">
                    {/* SSH Host */}
                    <FormField label="SSH Host" error={errors.sshHost?.message}>
                      <Input
                        {...register('sshHost')}
                        placeholder="bastion.example.com"
                        error={!!errors.sshHost}
                      />
                    </FormField>

                    {/* SSH Port */}
                    <FormField label="SSH Port" error={errors.sshPort?.message}>
                      <Input
                        {...register('sshPort')}
                        type="number"
                        min="1"
                        max="65535"
                        error={!!errors.sshPort}
                      />
                    </FormField>

                    {/* SSH Username */}
                    <FormField label="SSH Username" error={errors.sshUsername?.message}>
                      <Input
                        {...register('sshUsername')}
                        placeholder="ssh_user"
                        error={!!errors.sshUsername}
                      />
                    </FormField>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Ketrics-Managed Connection Details */}
          {isManaged && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-4">Managed Database</h3>
              <FormField label="Select Managed Database" required error={errors.managedSqlDbCode?.message}>
                <Select
                  {...register('managedSqlDbCode')}
                  error={!!errors.managedSqlDbCode}
                >
                  <option value="">Select a managed database...</option>
                  {availableManagedDbs.map((db) => (
                    <option key={db.managedSqlDbCode} value={db.managedSqlDbCode}>
                      {db.managedSqlDbName} ({db.managedSqlDbCode})
                    </option>
                  ))}
                </Select>
              </FormField>
              {availableManagedDbs.length === 0 && (
                <p className="mt-2 text-xs text-gray-500">
                  No managed databases available. Create a managed SQL database first.
                </p>
              )}
              <Alert variant="info" className="mt-4">
                Ketrics-managed connections use IAM-based authentication. No credentials are required — the platform manages access securely.
              </Alert>
            </div>
          )}

          {/* Status */}
          <div className="flex items-center">
            <Checkbox
              id="isActive"
              {...register('isActive')}
              defaultChecked
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-900">
              Active (connection is available for use)
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Create Connection
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
